/**
 * @fileoverview Module catalog status policy — the shared decision rules for
 * whether a catalog module may be enabled on a tenant.
 * @module core-domain/modules/moduleStatus
 *
 * Responsibilities:
 * - Identify deprecated catalog entries
 * - Decide whether a module may be newly enabled
 * - Decide whether a catalog entry defaults to enabled
 *
 * Dependencies: `@firesquirrel/sync-config` (`canonicalModuleCode`, the shared
 *   tenant module code alias table); otherwise pure helpers — hosts inject
 *   catalog entries.
 *
 * Architecture:
 * - Shared by every host that renders module-selection UI (system-web
 *   department edit, onboarding wizard). Server-side creation validation in
 *   api/system-api tenantModuleResolver enforces this contract at the
 *   provisioning boundary.
 *
 * Notes:
 * - 'deprecated' means "no new use": an entry that is already enabled may be
 *   disabled, but an entry that is not enabled cannot be newly enabled.
 */

import { canonicalModuleCode } from '@firesquirrel/sync-config';

export const MODULE_STATUS_DEPRECATED = 'deprecated';

export function isDeprecatedModuleStatus(status: string | null | undefined): boolean {
  // Normalize before comparing: catalog entries imported from JSON or written
  // by legacy tooling may carry case/whitespace variants that would otherwise
  // slip past the deprecated gate and stay enableable.
  return status?.trim().toLowerCase() === MODULE_STATUS_DEPRECATED;
}

/**
 * @contract ModuleCatalogStatusEntry
 * Purpose: Minimal catalog shape needed for status-policy decisions.
 * Fields:  status — catalog lifecycle status; enabledByDefault — seed default
 * Rules:   both fields optional; absent status is treated as non-deprecated
 * Sync:    catalog is system-owned; propagated to hosts via listModules
 */
export interface ModuleCatalogStatusEntry {
  status?: string | null;
  enabledByDefault?: boolean | null;
}

/**
 * Whether the module may be enabled (turned on) right now.
 * A deprecated entry returns false — enabling it is "new use". Callers combine
 * this with their own invariants (e.g. last-enabled-module lock) for the full
 * disabled state of a checkbox.
 */
export function canEnableModuleSelection(
  entry: ModuleCatalogStatusEntry | null | undefined,
): boolean {
  return !isDeprecatedModuleStatus(entry?.status);
}

/**
 * Whether a catalog entry defaults to enabled when no explicit selection
 * exists. Deprecated entries never default on — seeding a default-on
 * deprecated module would generate a selection the creation contract rejects.
 */
export function isModuleEnabledByDefault(entry: ModuleCatalogStatusEntry): boolean {
  return Boolean(entry.enabledByDefault) && !isDeprecatedModuleStatus(entry.status);
}

/**
 * @contract TenantModuleSelectionEntry
 * Purpose: Minimal shape of one entry in a tenant document's `modules[]`
 *   selection array.
 * Fields:  code — catalog module code; isEnabled — tenant's on/off choice
 * Rules:   entries without a non-empty string code or without `isEnabled === true`
 *   never count as enabled
 * Sync:    the selection is system-owned; tenant-web reads it via the synced
 *   `system_tenant_ref` doc, mobile-pwa via the synced `tenant_modules`
 *   collection.
 */
export interface TenantModuleSelectionEntry {
  code?: string;
  isEnabled?: boolean;
  [key: string]: unknown;
}

/**
 * Canonicalize one tenant `modules[].code` value to the catalog spelling.
 * Delegates to the shared alias table in `@firesquirrel/sync-config` so web
 * gating and the server resolver map drifted codes identically. Canonical and
 * unrecognized codes pass through unchanged.
 */
export function canonicalizeTenantModuleCode(code: unknown): string {
  return canonicalModuleCode(code);
}

/**
 * Extracts the enabled module codes from a tenant `modules[]` selection —
 * the shared derivation used by every host that gates UI on enabled modules.
 * Codes are canonicalized via `canonicalizeTenantModuleCode` and deduplicated
 * so drifted spellings stored on the tenant document still satisfy the
 * canonical code checks used across the UI.
 * Returns [] when the selection is absent or malformed; callers that need to
 * distinguish "no config" from "nothing enabled" must check for a missing
 * selection BEFORE calling this.
 */
export function extractEnabledModuleCodes(modules: unknown): string[] {
  if (!Array.isArray(modules)) return [];
  const codes = (modules as TenantModuleSelectionEntry[])
    .filter((m) => m?.isEnabled === true && typeof m.code === 'string' && m.code.trim() !== '')
    .map((m) => canonicalizeTenantModuleCode(m.code));
  return Array.from(new Set(codes));
}

/**
 * Extracts enabled module codes from a Dexie result set of tenant
 * documents — picks the first non-deleted document and reads its `modules[]`
 * selection. Shared by tenant-web (`system_tenant_ref` rows) and mobile-pwa
 * (`tenant_modules` projection rows) so both hosts apply the identical
 * doc-selection and enabled-code contract.
 * Returns [] when no live document carries a selection.
 */
export function extractEnabledModuleCodesFromDocs(docs: unknown): string[] {
  const list = Array.isArray(docs) ? docs : [];
  const tenant = list.find((doc) => !(doc as { _deleted?: boolean })?._deleted) as
    | { modules?: unknown }
    | undefined;
  return extractEnabledModuleCodes(tenant?.modules);
}

/**
 * @process hasMatchingEnabledModule
 * Steps:
 * 1. Normalize `categoryModules` into a trimmed string array; an empty array
 *    means the category is unrestricted and returns `true`.
 * 2. Build a lookup set containing both raw and `canonicalizeTenantModuleCode`
 *    forms of `enabledModules`.
 * 3. Return `true` when any category module matches raw or canonicalized.
 * Inputs: categoryModules (stored on a lookup category), enabledModules.
 * Outputs: boolean.
 * Failure Handling: null/undefined inputs are treated as empty.
 * Idempotency: REQUIRED — pure function.
 */
export function hasMatchingEnabledModule(
  categoryModules: readonly (string | null | undefined)[] | string | null | undefined,
  enabledModules: Iterable<string> | null | undefined,
): boolean {
  const rawList = Array.isArray(categoryModules)
    ? categoryModules
    : typeof categoryModules === 'string'
      ? [categoryModules]
      : [];
  const trimmedCategory = rawList.map((m) => String(m ?? '').trim()).filter(Boolean);
  if (trimmedCategory.length === 0) return true;

  const enabledSet = new Set<string>();
  for (const mod of enabledModules ?? []) {
    const raw = String(mod ?? '').trim();
    if (!raw) continue;
    enabledSet.add(raw);
    const canonical = canonicalizeTenantModuleCode(raw);
    if (canonical) enabledSet.add(canonical);
  }
  return trimmedCategory.some((mod) => {
    if (enabledSet.has(mod)) return true;
    const canonical = canonicalizeTenantModuleCode(mod);
    return Boolean(canonical && enabledSet.has(canonical));
  });
}

/**
 * @process buildModuleCatalogLabelMap
 * Steps: Index catalog `{ key, label }` entries by both raw `key` and
 *   `canonicalizeTenantModuleCode(key)` so alias-coded categories and
 *   canonicalized enabled-module codes both resolve to the catalog label.
 * Inputs: catalog options.
 * Outputs: Record<string, string>.
 * Failure Handling: skips empty keys.
 * Idempotency: REQUIRED — pure function.
 */
export function buildModuleCatalogLabelMap(
  catalog: ReadonlyArray<{ key: string; label: string }> | null | undefined,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const option of catalog ?? []) {
    const key = String(option?.key ?? '').trim();
    if (!key) continue;
    map[key] = option.label;
    const canonical = canonicalizeTenantModuleCode(key);
    if (canonical && !(canonical in map)) {
      map[canonical] = option.label;
    }
  }
  return map;
}

/**
 * @process resolveEnabledCategoryModuleOptions
 * Steps: Match each enabled module code against the tenant's module catalog by
 *   exact key or canonicalized code, preserving the catalog's stored `key` and
 *   `label` when present so category metadata saves the original catalog code.
 * Inputs: catalog options and enabled module codes.
 * Outputs: sorted `{ key, label, enabled }` array.
 * Failure Handling: falls back to `{ key, label: key, enabled: true }` when a
 *   code is absent from the catalog.
 * Idempotency: REQUIRED — pure function.
 */
export function resolveEnabledCategoryModuleOptions(
  catalog: ReadonlyArray<{ key: string; label: string; enabled?: boolean }> | null | undefined,
  enabledModules: Iterable<string> | null | undefined,
): Array<{ key: string; label: string; enabled: boolean }> {
  const byExact = new Map<string, { key: string; label: string; enabled: boolean }>();
  const byCanonical = new Map<string, { key: string; label: string; enabled: boolean }>();
  for (const option of catalog ?? []) {
    const key = String(option?.key ?? '').trim();
    if (!key) continue;
    const normalized = { key, label: option.label, enabled: option.enabled ?? true };
    byExact.set(key, normalized);
    const canonical = canonicalizeTenantModuleCode(key);
    if (canonical && !byCanonical.has(canonical)) {
      byCanonical.set(canonical, normalized);
    }
  }

  const out = new Map<string, { key: string; label: string; enabled: boolean }>();
  for (const rawCode of enabledModules ?? []) {
    const code = String(rawCode ?? '').trim();
    if (!code) continue;
    const canonical = canonicalizeTenantModuleCode(code);
    const matched = byExact.get(code) ?? (canonical ? byCanonical.get(canonical) : undefined) ?? {
      key: code,
      label: code,
      enabled: true,
    };
    if (!out.has(matched.key)) {
      out.set(matched.key, matched);
    }
  }
  return Array.from(out.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * @process isModuleOptionSelected
 * Steps: Check whether `optionKey` is present in `selected` by exact match or
 *   canonicalized tenant module code equality.
 * Inputs: selected module codes, optionKey.
 * Outputs: boolean.
 * Failure Handling: empty optionKey returns false.
 * Idempotency: REQUIRED — pure function.
 */
export function isModuleOptionSelected(
  selected: readonly string[] | null | undefined,
  optionKey: string,
): boolean {
  const target = String(optionKey ?? '').trim();
  if (!target) return false;
  const targetCanonical = canonicalizeTenantModuleCode(target);
  return (selected ?? []).some((item) => {
    const raw = String(item ?? '').trim();
    return raw === target || (Boolean(targetCanonical) && canonicalizeTenantModuleCode(raw) === targetCanonical);
  });
}

/**
 * @process toggleModuleOptionSelection
 * Steps: If `optionKey` (or its canonical alias) is already in `selected`,
 *   remove matching entries; otherwise append `optionKey` while preserving all
 *   other stored codes verbatim.
 * Inputs: selected module codes, optionKey.
 * Outputs: updated string[].
 * Failure Handling: empty optionKey returns a shallow copy of `selected`.
 * Idempotency: NOT REQUIRED — toggle operation.
 */
export function toggleModuleOptionSelection(
  selected: readonly string[] | null | undefined,
  optionKey: string,
): string[] {
  const list = [...(selected ?? [])];
  const target = String(optionKey ?? '').trim();
  if (!target) return list;
  const targetCanonical = canonicalizeTenantModuleCode(target);
  if (isModuleOptionSelected(list, target)) {
    return list.filter((item) => {
      const raw = String(item ?? '').trim();
      return raw !== target && (!targetCanonical || canonicalizeTenantModuleCode(raw) !== targetCanonical);
    });
  }
  return [...list, target];
}
